import { Record as record, List } from 'immutable';
import moment from 'moment';

import config from '../../config';

import { resolve } from '../../helpers/LinkMapper';
import OrderTripFares from './TripFares';
import { OrderTripTypes, TripBookingStatus } from '../../constants/AppConstants';
import trainTime from '../../lib/TrainTime';
import LeadPassenger from './LeadPassenger';
import Station from '../Station';
import FavouritesStore from '../../stores/FavouritesStore';

const defaults = {
    id: null,
    bookingStatus: null,
    bookingReference: null,
    fares: new OrderTripFares(),
    itso: false,
    selfPrintPassenger: new LeadPassenger()
};


class OrderTrip extends record(defaults) {

    isSmartcard () {
        return this.itso;
    }

    getType () {
        if (this.hasDaySaveFare()) { return OrderTripTypes.DAYSAVE; }
        if (this.hasRoversRangersFare()) { return OrderTripTypes.ROVERSRANGERS; }
        if (this.fares.season) { return OrderTripTypes.SEASON; }
        if (this.fares.return) { return OrderTripTypes.RETURN; }
        if (this.hasFareOnlyFare()) { return OrderTripTypes.FARE_ONLY; }
        if (this.fares.outwardSingle && this.fares.returnSingle) { return OrderTripTypes.RETURN_SINGLES; }
        if (this.fares.returnSingle) { return OrderTripTypes.RETURN_SINGLE; }

        return OrderTripTypes.SINGLE;
    }

    isSingle () {
        return this.getType() === OrderTripTypes.SINGLE;
    }

    isFareOnly () {
        return this.getType() === OrderTripTypes.FARE_ONLY;
    }

    isSeason () {
        return this.getType() === OrderTripTypes.SEASON;
    }

    canAmend () {
        const minsFromNow = moment().add(config.get('amendMinMinutesLeft'), 'minutes');
        const hasValidAdvanceFares =
            this.getProducts().some((fare) => fare.isAdvance() && fare.hasAmendableTickets() && fare.getDepartureTime() && fare.getDepartureTime().isAfter(minsFromNow));
        return !this.isSeason() && hasValidAdvanceFares;
    }

    needsJourney () {
        const type = this.getType();
        return type !== OrderTripTypes.DAYSAVE && type !== OrderTripTypes.SEASON
            && type !== OrderTripTypes.ROVERSRANGERS && !this.isTransPennineStaffRover();
    }

    hasFare (fare) {
        return this.getJourneyFares().contains(fare);
    }

    getJourneyFares () {
        const fares = this.fares;

        let journeyFares = new List();
        switch (this.getType()) {
            case OrderTripTypes.DAYSAVE:
                journeyFares = List.of(this.getDaySaveFare());
                break;
            case OrderTripTypes.ROVERSRANGERS:
                journeyFares = List.of(this.getRoversRangersFare());
                break;
            case OrderTripTypes.FARE_ONLY:
                journeyFares = List.of(this.getFareOnlyFare());
                break;
            case OrderTripTypes.RETURN:
                journeyFares = List.of(fares.return);
                break;
            case OrderTripTypes.RETURN_SINGLES:
                journeyFares = List.of(fares.outwardSingle, fares.returnSingle);
                break;
            case OrderTripTypes.RETURN_SINGLE:
                journeyFares = List.of(fares.returnSingle);
                break;
            case OrderTripTypes.SEASON:
                journeyFares = List.of(fares.season);
                break;
            default:
                journeyFares = List.of(fares.outwardSingle);
        }

        return journeyFares.filter((x) => !!x);
    }

    getFareByTicketId (id) {
        return this.getJourneyFares().concat(this.getPlusBusFares()).find(fare => fare.hasTicket(id));
    }

    getFaresIncludedInFavourites (withDelivery = false) {
        const favourites = FavouritesStore.getFavourites();
        return this.getJourneyFares().filter(fare =>
            favourites.filter(favourite =>
                favourite.matchFare(fare, this) && (
                    withDelivery ? favourite.delivery : true
                )
            ).isEmpty() === false
        );
    }

    includesFavouriteFare (withDelivery = false) {
        return this.getFaresIncludedInFavourites(withDelivery).size > 0;
    }

    getPhotocardNumber () {
        if (!this.hasSeasonFare) { return null; }

        const seasonFare = this.getSeasonFare();
        if (!seasonFare.hasPhotocardNumber()) { return null; }

        return seasonFare.getPhotocardNumber();
    }

    hasPhotocardNumber () {
        return !!this.getPhotocardNumber();
    }

    getTotalPrice () {
        const fares = this.getJourneyFares().reduce((out, fare) => out + fare.getTotalPrice(), 0);
        const plusBus = this.hasPlusBus() ? this.getPlusBus().getTotalPrice() : 0;

        return fares + plusBus;
    }

    getDepartureTime () {
        return this.getOutwardJourney().getDepartureTime();
    }

    getEarliestTime () {
        const journey = this.getOutwardJourney();
        return this.needsJourney() && journey.getDepartureTime() || journey.validFrom;
    }

    getOutwardJourney () {
        const fare = this.getOutwardFare();
        return fare.outwardJourney || null;
    }

    getReturnJourney () {
        if (this.fares.returnSingle) {
            return this.fares.returnSingle.outwardJourney;
        }

        if (this.fares.return) {
            return this.fares.return.returnJourney;
        }

        return null;
    }

    hasReturnJourney () {
        return !!this.getReturnJourney();
    }

    getJourneys () {
        return new List([
            this.getOutwardJourney(),
            this.getReturnJourney()
        ]).filter(Boolean);
    }

    getCleanId () {
        let regex = /orders\/.*\/trips\/(.*)/;
        let matches = this.id.match(regex);
        if (matches) {
            return matches[1];
        }
        return this.id;
    }

    getPlusBus () {
        return this.fares.plusBus;
    }

    getPlusBusFares () {
        return this.fares.plusBus.getFares();
    }

    hasPlusBus () {
        return !!(this.fares.plusBus && this.getPlusBus().count())
            && this.getPlusBusFares()
            .some(fare => fare.getPlusBusTickets()
                  .some(ticket => !ticket.isRefunded));
    }

    getDaySaveFare () {
        return this.fares.getDaySaveFare();
    }

    getRoversRangersFare () {
        return this.fares.getRoversRangersFare();
    }

    getSmartCarnetFare () {
        return this.fares.getSmartCarnetFare();
    }

    hasDaySaveFare () {
        return !!this.getDaySaveFare();
    }

    hasRoversRangersFare () {
        return !!this.getRoversRangersFare();
    }

    hasFareOnlyFare () {
        return !!this.getFareOnlyFare();
    }

    hasSmartCarnetFare () {
        return !!this.getSmartCarnetFare();
    }

    getFareOnlyFare () {
        return this.fares.fareOnly;
    }

    hasSeasonFare () {
        return !!this.getSeasonFare();
    }

    getSeasonFare () {
        return this.fares.season;
    }

    getOutwardFare () {
        if (this.hasFareOnlyFare()) {
            return this.getFareOnlyFare();
        }
        if (this.hasDaySaveFare()) {
            return this.getDaySaveFare();
        }
        if (this.hasSeasonFare()) {
            return this.getSeasonFare();
        }
        if (this.fares.return) {
            return this.fares.return;
        }

        if (this.fares.outwardSingle) {
            return this.fares.outwardSingle;
        }
        return null;

    }

    seatsReserved () {
        return this.bookingStatus === TripBookingStatus.OK;
    }

    seatsNotRequired () {
        return this.bookingStatus === TripBookingStatus.FAILED_NOT_REQUIRED;
    }

    isPrinted () {
        if (this.isSmartcard()) {
            return undefined;
        }
        const fares = this.getJourneyFares();
        return fares.every((fare) => fare.isPrinted());
    }

    isUnprinted () {
        if (this.isSmartcard()) {
            return undefined;
        }
        const fares = this.getJourneyFares();
        return fares.some((fare) => fare.isUnprinted());
    }

    // this method does not consider eTickets:
    isTooSoonToCollect () {
        if (this.isSmartcard()) {
            return trainTime().add(2, 'hours').isAfter(this.getDepartureTime());
        }
        return trainTime().add(30, 'minutes').isAfter(this.getDepartureTime());
    }

    hasTicket (id) {
        return this.amendedTickets.some(fare => fare.newTicket === id);
    }

    isReturn () {
        return !!this.getOutwardJourney() && !!this.getReturnJourney();
    }

    isOpenReturn () {
        return this.fares.isOpenReturn();
    }

    getTickets () {
        return this.getJourneyFares().map((fare) => fare && fare.tickets).flatten(true);
    }

    getTicketsWithTicketTypeAndValidity () {
        return this.getJourneyFares().map((fare) => fare &&
            fare.tickets
                .map(t => t.set('ticketType', fare.ticketType.code))
                .map(t => t.set('travelStartDate', fare.getValidFrom()))
            ).flatten(true);
    }

    getProducts () {
        return this.getJourneyFares().concat(this.getPlusBus().getFares());
    }

    hasRefundableTicket () {
        return this.getTickets().some((t) => t.isRefundable)
            || this.getPlusBusFares().some(f => f.hasRefundableTickets());
    }

    hasDownloadableTickets () {
        return this.getTickets().some((t) => t.attachment);
    }

    hasJCPRailcard () {
        return this.getTickets().some((t) => t.railcard && t.railcard.code === 'JCP');
    }

    has1617Railcard () {
        return this.getTickets().some((t) => t.railcard && t.railcard.code === 'TSU');
    }

    getFirstOrigin () {
        if (this.hasFareOnlyFare()) {
            return this.getFareOnlyFare().origin;
        }

        if (this.hasDaySaveFare() || this.hasRoversRangersFare()) {
            return new Station();
        }

        return this.hasSeasonFare()
            ? this.getSeasonFare().origin
            : this.getOutwardJourney().legs.first()
                ? this.getOutwardJourney().legs.first().origin
                : this.getOutwardFare().origin; // Open Golf does not have legs use origin from the fare instead
    }

    getDestination () {
        const journey = this.getOutwardJourney();
        if (journey.legs.size) {
            return journey.legs.last().destination;
        }
        const fare = this.getOutwardFare();
        return fare && fare.destination || new Station();
    }

    needsPassengers () {
        return this.hasRoversRangersFare();
    }

    hasPassengers () {
        return this.getTickets().every((ticket) => ticket.passenger !== null);
    }

    hasDiscount (discount) {
        return this.getTickets().some( ticket => ticket.hasDiscount(discount));
    }

    canClaimDelayRepay() {
        // Return false for open return and other trip types
        const notApplicable = [
            OrderTripTypes.SEASON,
            OrderTripTypes.DAYSAVE,
            OrderTripTypes.ROVERSRANGERS,
        ];
        if (this.isOpenReturn() || notApplicable.includes(this.getType())) {
            return false;
        }

        const outwardFare = this.getOutwardFare();
        const returnFare = this.fares.returnSingle || this.fares.return;
        const hasClaimableTickets = fare => fare && fare.tickets.some(ticket => !ticket.refunded);

        const journeys = [
            hasClaimableTickets(outwardFare) ? outwardFare.outwardJourney : null,
            hasClaimableTickets(returnFare)
                ? returnFare.isReturn()
                    ? returnFare.returnJourney
                    : returnFare.outwardJourney
                : null,
        ];

        // Can only claim for fares with journeys that might have been delayed (in the past)
        const pastJourneys = journeys.filter(
            journey => {
                const departure = journey && journey.getDepartureTime();
                return (
                    departure &&
                    departure.isBefore() &&
                    departure.isAfter(
                        trainTime()
                            .startOf('day')
                            .subtract(config.get('delayRepayMaxTicketDays'), 'days'),
                    )
                );
            }
        );

        if (pastJourneys.length >= 0) {
            // At least one leg in the journey must operate on a valid TOC for this brand
            const tocGroup = config.get('tocGroup');
            const isApplicableToBrand = pastJourneys.some(journey => {
                const legs = journey.legs.filter(leg => leg.toc);
                return legs.some(leg => leg.toc && tocGroup.includes(leg.toc.code));
            });
            return isApplicableToBrand;
        }

        return false;
    }

    isFlexi () {
        return this.fares.season && this.fares.season.smartTicketType === 'FL1';
    }

    isTransPennineStaffRover() {
        const origin = this.getFirstOrigin();
        const destination = this.getDestination();
        return Boolean(
            origin && destination &&
            origin.get('nlc') === 'Z031' &&
            destination.get('nlc') === 'Z032'
        );
    }

    static fromJSON (json) {
        return new OrderTrip({
            id: json.id,
            bookingStatus: json.bookingStatus,
            bookingReference: json.bookingReference,
            fares: resolve(json.fares).as(OrderTripFares),
            itso: resolve(json.itso).with(Boolean),
            selfPrintPassenger: resolve(json.selfPrintPassenger).as(LeadPassenger) || new LeadPassenger()
        });
    }
}


export default OrderTrip;



// WEBPACK FOOTER //
// ./src/records/order/Trip.js